import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import {
  SIEGE_COLS,
  SIEGE_ROWS,
  type SiegeRuntime,
} from '../structures/siege/siegePhysics';
import { getSiegePageSession, type SiegePageSession } from '../core/pageSessions/siegePageSession';
import { bakeSpallAtlas } from '../structures/siege/damage/atlas';
import { createChamferedBoxGeometry } from '../structures/siege/damage/brickGeometry';
import {
  createBrickMaterial,
  createShardExteriorMaterial,
  stampShardVariance,
  type BrickMaterialHandles,
} from '../structures/siege/damage/brickMaterial';
import { CLAY_DEEP, CLAY_SALMON, weatheredTint } from '../structures/siege/damage/palette';
import { addSurfaceRestPositionAttribute, cellToBufferGeometry } from '../services/fracture/cellGeometry';
import { hash01 } from '../structures/siege/damage/hash';
import {
  RESIDUE_MAP_H,
  RESIDUE_MAP_W,
  bakeResidueMap,
  emptyResidue,
} from '../structures/siege/damage/residue';
import type { DamageLedger } from '../structures/siege/damage/ledger';
import type { WallGrid } from '../structures/siege/damage/wallGrid';
import {
  createParticleWorld,
  getProfile,
  type ParticleMaterialProfile,
  type ParticleWorld,
} from '../effects/particles';
import { useViewportWidth } from '../hooks/useViewportWidth';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import './SiegeLab.css';

const STATE_CODE: Record<string, number> = {
  intact: 0,
  spalled: 1,
  cracked: 2,
  rim: 3,
  removed: 4,
};

const CANNON_POS = new THREE.Vector3(-4.8, 0, 7.4);
const MUZZLE_LOCAL = new THREE.Vector3(0, 0.62, 1.06);
const ARENA = { minX: -11, maxX: 11, minZ: -9, maxZ: 9 };
const MOBILE_BREAKPOINT = 768;
const KEY_LIGHT = { x: -10, y: 10.5, z: 8 } as const;
/** Cool rim: higher and more behind so the wall crown silhouettes from the default camera. */
const RIM_LIGHT = { x: 1.2, y: 14.5, z: -13.5 } as const;
/** Close warm point kickers along the crown (no target wiring). */
const CROWN_KICKER_X = [-3.6, -2.4, -1.2, 0, 1.2, 2.4, 3.6] as const;
/** Same azimuth as KEY_LIGHT, lower elevation so six-way front/back lobes can read. */
const DUST_LIGHT = { x: -10, y: 5.5, z: 8 } as const;
const DETACH_BURST_CAP = 6;
const SIEGE_FOG_COLOR = '#c9925e';
const SIEGE_FOG_DENSITY = 0.0082;
const SIEGE_GROUND_COLOR = 0x6a5340;
const SIEGE_ENV_INTENSITY = 0.34;
const SIEGE_EXPOSURE = 1.12;
const SIEGE_FAKE_SHADOW = 0.28;

const LAB_BRICK = getProfile('brick');
/** Siege-only brick burst: more lingering haze so six-way shading can read. */
const SIEGE_BRICK: ParticleMaterialProfile = {
  ...LAB_BRICK,
  fineFraction: 0.76,
  cloudLinger: 2.6,
  signature: {
    ...LAB_BRICK.signature,
    hazeFraction: 0.24,
    hazeLinger: 6.5,
    gritFraction: 0.75,
    // Real hull fragments replace whole-brick particle chunks. Leaving the
    // lab chunk emitter on perched 48 BoxGeometry cuboids on the ghost wall
    // roof (raised base elevation ≈ wall top).
    chunkFraction: 0,
    chunkCountRange: [0, 0],
    chunkCap: 0,
    chunkStragglerRange: [0, 0],
    chipFraction: 0,
    chipCap: 0,
  },
};

function applyDustLight(pw: ParticleWorld | null, scene: THREE.Scene): void {
  if (!pw) return;
  pw.setSixWay(true);
  pw.setLightDir(DUST_LIGHT.x, DUST_LIGHT.y, DUST_LIGHT.z);
  const fines = scene.getObjectByName('particle-lab-fines') as THREE.Mesh | undefined;
  const u = (fines?.material as THREE.ShaderMaterial | undefined)?.uniforms;
  if (!u) return;
  if (u.uLightColor) u.uLightColor.value.set('#ffd2a8').multiplyScalar(1.65);
  if (u.uHemiSky) u.uHemiSky.value.set('#6a4a38').multiplyScalar(0.2);
  if (u.uHemiGround) u.uHemiGround.value.set('#1a100c').multiplyScalar(0.26);
}

interface SiegeBrickSample {
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  detached: boolean;
  retired: boolean;
  awake?: boolean;
}

interface SiegeFragmentSample {
  x: number;
  y: number;
  z: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  retired: boolean;
  settled?: boolean;
  awake?: boolean;
  speed?: number;
  bodyType?: string | null;
  innerFaces?: number;
  outerFaces?: number;
  px?: number;
  py?: number;
}

interface SiegeDebrisDump {
  fragments: SiegeFragmentSample[];
  chunks: Array<{ x: number; y: number; z: number; sx: number; sy: number; sz: number; px?: number; py?: number }>;
  chips: Array<{ x: number; y: number; z: number; sx: number; sy: number; sz: number; px?: number; py?: number }>;
  bricks: Array<{
    x: number;
    y: number;
    z: number;
    detached: boolean;
    retired: boolean;
    awake?: boolean;
    px?: number;
    py?: number;
  }>;
}

interface SiegeHudState {
  ready: boolean;
  physicsReady: boolean;
  fps: number;
  awakeCount: number;
  awakeBrickCount: number;
  detachedCount: number;
  stamps: number;
  bursts: number;
  simTime: number;
  stepMs: number;
}

interface SiegeBridge {
  fireAt(x: number, y: number, z: number): void;
  reset(): void;
  dumpScene?(): string[];
  hideByName?(name: string, visible: boolean): void;
  setFakeShadow?(on: boolean): void;
  setKeyCastShadow?(on: boolean): void;
  setReceiveShadow?(name: string, on: boolean): void;
  setToneMapping?(kind: 'aces' | 'agx', exposure: number): void;
  dumpGroundUniforms?(): Record<string, unknown> | null;
  sampleBrick?(i: number): SiegeBrickSample | null;
  sampleFragment?(i: number): SiegeFragmentSample | null;
  dumpDebris?(): SiegeDebrisDump | null;
  sampleHeight?(x: number, z: number): { deposit: number; granular: number; film: number } | null;
  dumpCloud?(): Record<string, unknown> | null;
  lastBurst?(): unknown;
  setLightDir?(x: number, y: number, z: number): void;
  setSixWay?(on: boolean): void;
  setGrainsVisible?(on: boolean): void;
}

declare global {
  interface Window {
    __siegeState?: Record<string, unknown>;
    __siegeProbe?: {
      fireAt(x: number, y: number, z: number): void;
      barrage(n?: number): void;
      reset(): void;
      setCharge(v: number): void;
      setSlowMo(v: boolean): void;
      setAutoOrbit(v: boolean): void;
      setCamera(px: number, py: number, pz: number, tx?: number, ty?: number, tz?: number): void;
      dumpScene(): string[];
      hideByName(name: string, visible: boolean): void;
      setFakeShadow(on: boolean): void;
      setKeyCastShadow(on: boolean): void;
      setReceiveShadow(name: string, on: boolean): void;
      setToneMapping(kind: 'aces' | 'agx', exposure: number): void;
      dumpGroundUniforms(): Record<string, unknown> | null;
      getCamera(): { px: number; py: number; pz: number; tx: number; ty: number; tz: number } | null;
      sampleBrick?(i: number): SiegeBrickSample | null;
      sampleFragment?(i: number): SiegeFragmentSample | null;
      dumpDebris?(): SiegeDebrisDump | null;
      sampleHeight?(x: number, z: number): { deposit: number; granular: number; film: number } | null;
      dumpCloud?(): Record<string, unknown> | null;
      lastBurst?(): unknown;
      setLightDir?(x: number, y: number, z: number): void;
      setSixWay?(on: boolean): void;
      setGrainsVisible?(on: boolean): void;
    };
  }
}

const PROJECT_SCRATCH = new THREE.Vector3();

function projectScreen(
  x: number,
  y: number,
  z: number,
  camera: THREE.Camera,
  width: number,
  height: number,
): { sx: number; sy: number } {
  PROJECT_SCRATCH.set(x, y, z).project(camera);
  return {
    sx: (PROJECT_SCRATCH.x * 0.5 + 0.5) * width,
    sy: (-PROJECT_SCRATCH.y * 0.5 + 0.5) * height,
  };
}

function dumpInstancedLive(
  scene: THREE.Scene,
  name: string,
  camera?: THREE.Camera,
  width = 1280,
  height = 720,
): Array<{ x: number; y: number; z: number; sx: number; sy: number; sz: number; px?: number; py?: number }> {
  const mesh = scene.getObjectByName(name) as THREE.InstancedMesh | undefined;
  const out: Array<{ x: number; y: number; z: number; sx: number; sy: number; sz: number; px?: number; py?: number }> = [];
  if (!mesh?.instanceMatrix) return out;
  const e = mesh.instanceMatrix.array as Float32Array;
  const n = mesh.count ?? 0;
  for (let i = 0; i < n; i += 1) {
    const o = i * 16;
    const sx = Math.hypot(e[o], e[o + 1], e[o + 2]);
    const sy = Math.hypot(e[o + 4], e[o + 5], e[o + 6]);
    const sz = Math.hypot(e[o + 8], e[o + 9], e[o + 10]);
    const x = e[o + 12];
    const y = e[o + 13];
    const z = e[o + 14];
    if (!(sx > 1e-4 && sy > 1e-4 && sz > 1e-4)) continue;
    if (!Number.isFinite(y) || y < -50) continue;
    const rec: { x: number; y: number; z: number; sx: number; sy: number; sz: number; px?: number; py?: number } = {
      x, y, z, sx, sy, sz,
    };
    if (camera) {
      const p = projectScreen(x, y, z, camera, width, height);
      rec.px = p.sx;
      rec.py = p.sy;
    }
    out.push(rec);
  }
  return out;
}

function fragmentFaceCounts(mesh: THREE.Mesh | undefined): { innerFaces: number; outerFaces: number } {
  const groups = mesh?.geometry?.groups ?? [];
  let outer = 0;
  let inner = 0;
  for (const g of groups) {
    const tris = Math.max(0, (g.count | 0) / 3);
    if ((g.materialIndex ?? 0) === 0) outer += tris;
    else inner += tris;
  }
  return { innerFaces: inner, outerFaces: outer };
}

function dumpFinesUniforms(scene: THREE.Scene): Record<string, unknown> | null {
  const fines = scene.getObjectByName('particle-lab-fines') as THREE.Mesh | undefined;
  const mat = fines?.material as THREE.ShaderMaterial | undefined;
  const u = mat?.uniforms;
  if (!u) return { finesFound: Boolean(fines), finesVisible: fines?.visible ?? null };
  const tex = (slot: { value?: THREE.Texture } | undefined) => {
    const t = slot?.value;
    if (!t) return null;
    const img = t.image as { width?: number; height?: number; src?: string } | undefined;
    return {
      name: t.name || null,
      w: img?.width ?? null,
      h: img?.height ?? null,
      imagePresent: Boolean(img),
      isDummy: Boolean(t.name && t.name.includes('dummy')) || img?.width === 1,
      src: typeof img?.src === 'string' ? img.src.slice(-64) : null,
    };
  };
  const L = u.uLightDir?.value as THREE.Vector3 | undefined;
  const C = u.uLightColor?.value as THREE.Color | undefined;
  const M = u.uCameraMatrixWorld?.value as THREE.Matrix4 | undefined;
  const e = M?.elements;
  const camRight = e ? { x: +e[0].toFixed(3), y: +e[1].toFixed(3), z: +e[2].toFixed(3) } : null;
  const camUp = e ? { x: +e[4].toFixed(3), y: +e[5].toFixed(3), z: +e[6].toFixed(3) } : null;
  const camFront = e ? { x: +e[8].toFixed(3), y: +e[9].toFixed(3), z: +e[10].toFixed(3) } : null;
  const LdotFront =
    L && camFront ? +(L.x * camFront.x + L.y * camFront.y + L.z * camFront.z).toFixed(3) : null;
  return {
    finesFound: true,
    finesVisible: fines?.visible ?? null,
    uSixWay: u.uSixWay?.value ?? null,
    uLightDir: L ? { x: +L.x.toFixed(4), y: +L.y.toFixed(4), z: +L.z.toFixed(4) } : null,
    uLightColor: C ? { r: C.r, g: C.g, b: C.b, hex: C.getHexString() } : null,
    camRight,
    camUp,
    camFront,
    LdotFront,
    tSixA: tex(u.tSixA),
    tSixB: tex(u.tSixB),
    tSixA2: tex(u.tSixA2),
    tSixB2: tex(u.tSixB2),
  };
}

function detectTier(): 'desktop' | 'mobile' {
  return typeof window !== 'undefined' && window.innerWidth < 720 ? 'mobile' : 'desktop';
}

function disposeFragmentMesh(mesh: THREE.Mesh): void {
  mesh.geometry.dispose();
}

function clearFragmentMeshes(
  group: THREE.Group | null,
  meshes: Map<number, THREE.Mesh>,
): void {
  for (const mesh of meshes.values()) {
    group?.remove(mesh);
    disposeFragmentMesh(mesh);
  }
  meshes.clear();
}

function makeSky(): THREE.Mesh {
  const geo = new THREE.SphereGeometry(150, 24, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      uTop: { value: new THREE.Color('#2c3658') },
      uHorizon: { value: new THREE.Color(SIEGE_FOG_COLOR) },
    },
    vertexShader: /* glsl */ `
      varying float vY;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vY = normalize(wp.xyz).y;
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uTop;
      uniform vec3 uHorizon;
      varying float vY;
      void main() {
        float t = pow(smoothstep(0.02, 0.55, vY), 0.82);
        gl_FragColor = vec4(mix(uHorizon, uTop, t), 1.0);
      }
    `,
  });
  mat.toneMapped = true;
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'siege-sky';
  mesh.frustumCulled = false;
  return mesh;
}

function makeGround(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(260, 260),
    new THREE.MeshStandardMaterial({
      color: SIEGE_GROUND_COLOR,
      roughness: 0.96,
      metalness: 0.01,
      envMapIntensity: 0.28,
    }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = -0.012;
  mesh.receiveShadow = true;
  mesh.name = 'siege-ground';
  return mesh;
}

function applySiegeGroundLook(ground: THREE.Object3D): void {
  const mesh = ground as THREE.Mesh;
  const gMat = mesh.material;
  if (!gMat || Array.isArray(gMat) || !('uniforms' in gMat)) return;
  const shaderMat = gMat as THREE.ShaderMaterial;
  const u = shaderMat.uniforms;
  if (u.uDepositOnly) u.uDepositOnly.value = 1;
  if (u.uDisplace) u.uDisplace.value = 0;
  if (u.uGroundColor) u.uGroundColor.value.set(SIEGE_GROUND_COLOR);
  if (u.uFilmTint) (u.uFilmTint.value as THREE.Vector3).set(0.82, 0.7, 0.52);
  if (u.uDustCover) u.uDustCover.value = 0.88;
  if (u.uShadowMul) (u.uShadowMul.value as THREE.Vector3).set(0.84, 0.78, 0.7);
  if (u.uShadowStrength) u.uShadowStrength.value = SIEGE_FAKE_SHADOW;
  if (u.uShadowCap) u.uShadowCap.value = 0.48;
  if (u.uDepositStain) u.uDepositStain.value = 1;
  if (u.uLightDir) u.uLightDir.value.set(KEY_LIGHT.x, KEY_LIGHT.y, KEY_LIGHT.z).normalize();
  if (u.uLightColor) u.uLightColor.value.set('#ffd2a8');
  shaderMat.toneMapped = false;
  shaderMat.transparent = true;
  shaderMat.premultipliedAlpha = true;
  // r183: MultiplyBlending errors out unless premultipliedAlpha is true, and
  // without a blend func the overlay replaces dest with near-white stain.
  shaderMat.blending = THREE.CustomBlending;
  shaderMat.blendEquation = THREE.AddEquation;
  shaderMat.blendSrc = THREE.DstColorFactor;
  shaderMat.blendDst = THREE.OneMinusSrcAlphaFactor;
  shaderMat.blendEquationAlpha = THREE.AddEquation;
  shaderMat.blendSrcAlpha = THREE.ZeroFactor;
  shaderMat.blendDstAlpha = THREE.OneFactor;
  shaderMat.depthWrite = false;
  shaderMat.depthTest = true;
  shaderMat.fog = false;
  shaderMat.polygonOffset = true;
  shaderMat.polygonOffsetFactor = -1;
  shaderMat.polygonOffsetUnits = -1;
  shaderMat.needsUpdate = true;
  mesh.renderOrder = 2;
  mesh.receiveShadow = false;
}

function makeCannon(): { group: THREE.Group; barrelPivot: THREE.Group; dispose(): void } {
  const group = new THREE.Group();
  group.name = 'siege-cannon';
  const iron = new THREE.MeshStandardMaterial({ color: 0x262a30, roughness: 0.45, metalness: 0.65 });
  const wood = new THREE.MeshStandardMaterial({ color: 0x4b3524, roughness: 0.85, metalness: 0.02 });
  const brass = new THREE.MeshStandardMaterial({ color: 0x8f7040, roughness: 0.35, metalness: 0.8 });

  const carriage = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.4, 1.34), wood);
  carriage.position.set(0, 0.34, -0.08);
  carriage.castShadow = true;
  group.add(carriage);

  const wheelGeo = new THREE.CylinderGeometry(0.36, 0.36, 0.09, 20);
  for (const sx of [-0.44, 0.44]) {
    const wheel = new THREE.Mesh(wheelGeo, wood);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(sx, 0.36, 0.12);
    wheel.castShadow = true;
    group.add(wheel);
  }

  const barrelPivot = new THREE.Group();
  barrelPivot.position.set(0, 0.62, 0);
  group.add(barrelPivot);

  const breech = new THREE.Mesh(new THREE.SphereGeometry(0.19, 18, 14), iron);
  breech.position.set(0, 0, -0.5);
  breech.castShadow = true;
  barrelPivot.add(breech);

  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.115, 0.155, 1.6, 20), iron);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0, 0.28);
  barrel.castShadow = true;
  barrelPivot.add(barrel);

  const muzzleRing = new THREE.Mesh(new THREE.CylinderGeometry(0.135, 0.135, 0.09, 20), brass);
  muzzleRing.rotation.x = Math.PI / 2;
  muzzleRing.position.set(0, 0, 1.01);
  barrelPivot.add(muzzleRing);

  return {
    group,
    barrelPivot,
    dispose() {
      carriage.geometry.dispose();
      wheelGeo.dispose();
      breech.geometry.dispose();
      barrel.geometry.dispose();
      muzzleRing.geometry.dispose();
      iron.dispose();
      wood.dispose();
      brass.dispose();
    },
  };
}

function makeFlashTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(64, 64, 2, 64, 64, 62);
    g.addColorStop(0, 'rgba(255,236,190,1)');
    g.addColorStop(0.35, 'rgba(255,176,90,0.85)');
    g.addColorStop(1, 'rgba(255,120,40,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
  }
  return new THREE.CanvasTexture(c);
}

function bakeSpallAtlasTexture(): THREE.DataTexture {
  const baked = bakeSpallAtlas();
  const tex = new THREE.DataTexture(baked.data, baked.width, baked.height, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 1;
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Writes the damage-shader instance attributes straight from the ledger,
 * mirroring wallView.sync(). Detached bricks render as flying clay-revealed
 * rubble: their ledger state is `removed`, which discards in-shader, so the
 * attribute pins them to the rim look while they tumble.
 */
function writeInstAttrs(
  mesh: THREE.InstancedMesh,
  ledger: DamageLedger,
  rt: SiegeRuntime,
  residue: ReturnType<typeof emptyResidue>,
): void {
  const instA = mesh.geometry.getAttribute('instanceA') as THREE.InstancedBufferAttribute;
  const instB = mesh.geometry.getAttribute('instanceB') as THREE.InstancedBufferAttribute;
  const instC = mesh.geometry.getAttribute('instanceC') as THREE.InstancedBufferAttribute;
  const instD = mesh.geometry.getAttribute('instanceD') as THREE.InstancedBufferAttribute;
  const instE = mesh.geometry.getAttribute('instanceE') as THREE.InstancedBufferAttribute;
  const instF = mesh.geometry.getAttribute('instanceF') as THREE.InstancedBufferAttribute;
  for (let i = 0; i < ledger.bricks.length; i += 1) {
    const b = ledger.bricks[i];
    let sCode = STATE_CODE[b.state] ?? 0;
    if (sCode === 4 && rt.detached[i]) sCode = 3;
    instA.setXYZW(i, sCode, hash01(b.seed, 1), b.atlasVariant, b.atlasRot);
    instB.setXYZW(i, b.cornerMask, b.edgeDarken, b.pockCount, b.throughCrack);
    instC.setXYZW(i, b.pockU[0], b.pockV[0], b.pockU[1], b.pockV[1]);
    instD.setXYZW(i, b.pockU[2], b.pockV[2], b.throughAngle, b.throughAngle2);
    instE.setXYZW(i, residue.film[i] ?? 0, residue.scorch[i] ?? 0, residue.crackVis[i] ?? 0, b.atlasMirror);
    instF.setXYZW(i, b.backSpall, b.backScorch, 0, 0);
  }
  instA.needsUpdate = true;
  instB.needsUpdate = true;
  instC.needsUpdate = true;
  instD.needsUpdate = true;
  instE.needsUpdate = true;
  instF.needsUpdate = true;
}

interface SiegeSceneProps {
  onHud: (patch: Partial<SiegeHudState>) => void;
  onReady: () => void;
  controlsRef: React.MutableRefObject<OrbitControlsImpl | null>;
  chargeRef: React.MutableRefObject<number>;
  slowMoRef: React.MutableRefObject<boolean>;
  autoOrbitRef: React.MutableRefObject<boolean>;
  bridgeRef: React.MutableRefObject<SiegeBridge | null>;
  apiRef: React.MutableRefObject<{ lastFanoutJson: string }>;
  sessionRef: React.MutableRefObject<SiegePageSession | null>;
}

function SiegeScene({
  onHud,
  onReady,
  controlsRef,
  chargeRef,
  slowMoRef,
  autoOrbitRef,
  bridgeRef,
  apiRef,
  sessionRef,
}: SiegeSceneProps) {
  const { gl, scene, camera } = useThree();
  const runtimeRef = useRef<SiegeRuntime | null>(null);
  const pworldRef = useRef<ParticleWorld | null>(null);
  const brickMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const fragmentGroupRef = useRef<THREE.Group | null>(null);
  const fragmentMeshesRef = useRef<Map<number, THREE.Mesh>>(new Map());
  const fragmentMatsRef = useRef<THREE.MeshStandardMaterial[] | null>(null);
  const ledgerRef = useRef<DamageLedger | null>(null);
  const gridRef = useRef<WallGrid | null>(null);
  const residueRef = useRef<{ data: Uint8Array; tex: THREE.DataTexture } | null>(null);
  const cannonRef = useRef<{ group: THREE.Group; barrelPivot: THREE.Group } | null>(null);
  const flashRef = useRef<{ sprite: THREE.Sprite; light: THREE.PointLight } | null>(null);
  const fireInternalRef = useRef<(target: THREE.Vector3) => boolean>(() => false);
  const resetInternalRef = useRef<() => void>(() => {});
  const dustDirRef = useRef<{ x: number; y: number; z: number }>({
    x: DUST_LIGHT.x,
    y: DUST_LIGHT.y,
    z: DUST_LIGHT.z,
  });

  const recoilRef = useRef(0);
  const burstCountRef = useRef(0);
  const fpsAccRef = useRef({ frames: 0, t0: performance.now(), fps: 60 });
  const lastBridgeRef = useRef(0);
  const rubbleTintsRef = useRef<Array<[number, number, number]>>([]);
  const tintAppliedRef = useRef<Uint8Array | null>(null);
  const attrsDirtyRef = useRef(true);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const tmpColor = useMemo(() => new THREE.Color(), []);
  const tmpVec = useMemo(() => new THREE.Vector3(), []);
  const physicsQuat = useMemo(() => new THREE.Quaternion(), []);
  const jitterQuat = useMemo(() => new THREE.Quaternion(), []);
  const jitterEuler = useMemo(() => new THREE.Euler(0, 0, 0, 'XYZ'), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const brickScaleRef = useRef({ x: 1, y: 1, z: 1 });
  const lastFanoutRef = useRef({ detached: 0, detachBursts: 0 });
  const keyLightRef = useRef<THREE.DirectionalLight>(null);
  const fakeShadowDefaultRef = useRef(SIEGE_FAKE_SHADOW);

  // Boot: one effect owns every imperative scene object.
  useLayoutEffect(() => {
    let cancelled = false;
    const disposables: Array<{ dispose(): void }> = [];

    const boot = async () => {
      const session = await getSiegePageSession();
      if (cancelled) return;
      sessionRef.current = session;
      const rt = session.runtime;
      runtimeRef.current = rt;

      const grid = rt.grid;
      const ledger = rt.ledger;
      gridRef.current = grid;
      ledgerRef.current = ledger;
      const spec = grid.spec;

      const pmrem = new THREE.PMREMGenerator(gl);
      pmrem.compileCubemapShader();
      const room = new RoomEnvironment();
      const envRT = pmrem.fromScene(room, 0.04);
      room.dispose();
      scene.environment = envRT.texture;
      scene.environmentIntensity = SIEGE_ENV_INTENSITY;
      disposables.push({
        dispose() {
          if (scene.environment === envRT.texture) scene.environment = null;
          scene.environmentIntensity = 1;
          envRT.dispose();
          pmrem.dispose();
        },
      });

      scene.add(makeGround());
      scene.add(makeSky());
      for (const x of CROWN_KICKER_X) {
        const kicker = new THREE.PointLight(0xffc89a, 20, 4.8, 2);
        kicker.name = `siege-top-kicker-${x}`;
        kicker.position.set(x, 3.06, 0.48);
        kicker.castShadow = false;
        scene.add(kicker);
        disposables.push({
          dispose() {
            scene.remove(kicker);
            kicker.dispose();
          },
        });
      }

      // Bricks: the damage module's shader, driven by physics transforms.
      const atlas = bakeSpallAtlasTexture();
      disposables.push(atlas);
      const handles: BrickMaterialHandles = createBrickMaterial(atlas, {
        x: spec.brickLength * 0.5,
        y: spec.brickHeight * 0.5,
        z: spec.brickDepth * 0.5,
      });
      handles.material.envMapIntensity = 0.55;
      const scaleX = spec.pitchX / spec.brickLength;
      const scaleY = spec.pitchY / spec.brickHeight;
      brickScaleRef.current = { x: scaleX, y: scaleY, z: 1 };
      handles.setMortar(
        (spec.joint * 0.5) / scaleX,
        (spec.joint * 0.5) / scaleY,
        spec.joint * 0.5,
      );
      handles.setCrownBoost(0.28);
      disposables.push(handles);

      const count = ledger.bricks.length;
      const geom = createChamferedBoxGeometry(spec.brickLength, spec.brickHeight, spec.brickDepth);
      disposables.push({
        dispose() {
          geom.dispose();
        },
      });
      const attrInit = () => new THREE.InstancedBufferAttribute(new Float32Array(count * 4), 4);
      const instA = attrInit();
      const instB = attrInit();
      const instC = attrInit();
      const instD = attrInit();
      const instE = attrInit();
      const instF = attrInit();
      geom.setAttribute('instanceA', instA);
      geom.setAttribute('instanceB', instB);
      geom.setAttribute('instanceC', instC);
      geom.setAttribute('instanceD', instD);
      geom.setAttribute('instanceE', instE);
      geom.setAttribute('instanceF', instF);

      const mesh = new THREE.InstancedMesh(geom, handles.material, count);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.name = 'siege-bricks';

      const residueData = new Uint8Array(RESIDUE_MAP_W * RESIDUE_MAP_H * 4);
      const residueTex = new THREE.DataTexture(residueData, RESIDUE_MAP_W, RESIDUE_MAP_H, THREE.RGBAFormat);
      residueTex.colorSpace = THREE.NoColorSpace;
      residueTex.magFilter = THREE.LinearFilter;
      residueTex.minFilter = THREE.LinearFilter;
      residueTex.generateMipmaps = false;
      residueTex.wrapS = THREE.ClampToEdgeWrapping;
      residueTex.wrapT = THREE.ClampToEdgeWrapping;
      residueTex.flipY = false;
      residueTex.needsUpdate = true;
      residueRef.current = { data: residueData, tex: residueTex };
      disposables.push(residueTex);

      // Same slab-space mapping the damage lab uses for its residue map.
      const padX = spec.pitchX * 0.55;
      const slabW = spec.actualWidth + padX;
      const slabH = spec.actualHeight + spec.joint * 2;
      const slabLeft = spec.originX + spec.actualWidth * 0.5 + padX * 0.15 - slabW * 0.5;
      const slabBottom = -spec.joint;
      handles.setResidue(residueTex, slabLeft, slabBottom, slabW, slabH);

      const rubbleTints: Array<[number, number, number]> = [];
      const tintApplied = new Uint8Array(count);
      for (let i = 0; i < count; i += 1) {
        const seed = ledger.bricks[i].seed;
        const w = weatheredTint(hash01(seed, 3), hash01(seed, 8), hash01(seed, 13));
        rubbleTints.push([
          Math.min(1, w[0] * 0.78 + CLAY_SALMON[0] * 0.3),
          Math.min(1, w[1] * 0.78 + CLAY_SALMON[1] * 0.3),
          Math.min(1, w[2] * 0.78 + CLAY_SALMON[2] * 0.3),
        ]);
        tmpColor.setRGB(w[0], w[1], w[2]);
        mesh.setColorAt(i, tmpColor);
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      rubbleTintsRef.current = rubbleTints;
      tintAppliedRef.current = tintApplied;

      scene.add(mesh);
      brickMeshRef.current = mesh;

      const wTint = weatheredTint(0.45, 0.4, 0.35);
      const outerMat = createShardExteriorMaterial(new THREE.Color(wTint[0], wTint[1], wTint[2]));
      outerMat.envMapIntensity = 0.5;
      const innerMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(
          CLAY_DEEP[0] * 0.28 + 0.035,
          CLAY_DEEP[1] * 0.2 + 0.028,
          CLAY_DEEP[2] * 0.18 + 0.022,
        ),
        roughness: 1,
        metalness: 0,
        flatShading: true,
        envMapIntensity: 0.4,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });
      fragmentMatsRef.current = [outerMat, innerMat];
      disposables.push(outerMat, innerMat);
      const prewarmGeom = new THREE.BoxGeometry(0.04, 0.04, 0.04);
      addSurfaceRestPositionAttribute(prewarmGeom);
      stampShardVariance(prewarmGeom, 0, 0.45, 0.4, 0.35);
      prewarmGeom.addGroup(0, 18, 0);
      prewarmGeom.addGroup(18, 18, 1);
      const prewarmMesh = new THREE.Mesh(prewarmGeom, [outerMat, innerMat]);
      prewarmMesh.position.set(0, -30, 0);
      prewarmMesh.frustumCulled = false;
      scene.add(prewarmMesh);
      const fragGroup = new THREE.Group();
      fragGroup.name = 'siege-fragments';
      scene.add(fragGroup);
      fragmentGroupRef.current = fragGroup;

      // Particles: one world over the yard arena; wall AABB baked as collider.
      const pworld = createParticleWorld(gl, {
        arenaBounds: ARENA,
        tier: detectTier(),
        chunkFloorOnlySettle: true,
        groundShadow: { splatAmt: 0.08, pointScale: 1.3, maxPoint: 8 },
        colliders: [
          {
            center: [0, spec.actualHeight * 0.5, 0],
            half: [
              spec.actualWidth * 0.5 + 0.05,
              spec.actualHeight * 0.5 + 0.05,
              spec.brickDepth * 0.5 + 0.03,
            ],
          },
        ],
      });
      pworld.attach(scene);
      pworldRef.current = pworld;
      applyDustLight(pworld, scene);
      const colliderDebug = scene.getObjectByName('particle-lab-colliders');
      if (colliderDebug) colliderDebug.visible = false;
      const pGround = scene.getObjectByName('particle-lab-ground');
      if (pGround) {
        pGround.visible = true;
        applySiegeGroundLook(pGround);
      }
      const arenaEdge = scene.getObjectByName('particle-lab-arena-edge');
      if (arenaEdge) arenaEdge.visible = false;
      for (let i = 0; i < 16; i += 1) {
        const boxtop = scene.getObjectByName(`particle-lab-boxtop-${i}`);
        if (boxtop) boxtop.visible = false;
      }
      const labVoid = scene.getObjectByName('particle-lab-void');
      if (labVoid) labVoid.visible = false;

      // Cannon + muzzle flash.
      const cannon = makeCannon();
      disposables.push(cannon);
      cannon.group.position.copy(CANNON_POS);
      scene.add(cannon.group);
      cannonRef.current = cannon;

      const flashTex = makeFlashTexture();
      disposables.push(flashTex);
      const flashMat = new THREE.SpriteMaterial({
        map: flashTex,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
      });
      disposables.push(flashMat);
      const flashSprite = new THREE.Sprite(flashMat);
      flashSprite.scale.setScalar(0.001);
      flashSprite.visible = false;
      scene.add(flashSprite);
      const flashLight = new THREE.PointLight(0xffb36b, 0, 9, 1.8);
      scene.add(flashLight);
      flashRef.current = { sprite: flashSprite, light: flashLight };

      gl.compile(scene, camera);
      scene.remove(prewarmMesh);
      prewarmGeom.dispose();

      const doFire = (target: THREE.Vector3): boolean => {
        const pw = pworldRef.current;
        const cn = cannonRef.current;
        const fl = flashRef.current;
        if (!pw || !cn || !fl) return false;
        const origin = MUZZLE_LOCAL.clone();
        cn.barrelPivot.localToWorld(origin);
        const dir = target.clone().sub(origin);
        dir.y += origin.distanceTo(target) * 0.012;
        dir.normalize();
        session.fireAt(
          { x: origin.x, y: origin.y, z: origin.z },
          { x: dir.x, y: dir.y, z: dir.z },
          chargeRef.current,
        );
        burstCountRef.current += 1;

        fl.sprite.position.copy(origin).addScaledVector(dir, 0.18);
        fl.light.position.copy(fl.sprite.position);
        fl.sprite.material.opacity = 1;
        fl.sprite.scale.setScalar(0.9 + chargeRef.current * 0.7);
        fl.sprite.visible = true;
        fl.light.intensity = 26 + chargeRef.current * 22;
        recoilRef.current = 1;

        pw.burst({
          position: [origin.x, origin.y, origin.z],
          direction: [dir.x, dir.y, dir.z],
          energy: 0.4,
          mass: 0.02,
          profile: getProfile('concrete'),
          surfaceNormal: [0, 1, 0],
          impact: { kind: 'missile', intensity: 0.22, blastFraction: 0.15 },
        });
        burstCountRef.current += 1;
        return true;
      };
      fireInternalRef.current = doFire;

      resetInternalRef.current = () => {
        session.reset();
        clearFragmentMeshes(fragmentGroupRef.current, fragmentMeshesRef.current);
        pworldRef.current?.reset();
        dustDirRef.current = { x: DUST_LIGHT.x, y: DUST_LIGHT.y, z: DUST_LIGHT.z };
        applyDustLight(pworldRef.current, scene);
        burstCountRef.current = 0;
        lastFanoutRef.current = { detached: 0, detachBursts: 0 };
        tintApplied.fill(0);
        attrsDirtyRef.current = true;
      };

      bridgeRef.current = {
        fireAt: (x, y, z) => {
          doFire(new THREE.Vector3(x, y, z));
        },
        reset: () => resetInternalRef.current(),
        dumpScene: () => {
          const lines: string[] = [];
          scene.traverse((o) => {
            const mesh = o as THREE.Mesh;
            const mat = mesh.material;
            const mats = Array.isArray(mat) ? mat : mat ? [mat] : [];
            const bits = mats.map((m, i) => {
              const sm = m as THREE.MeshStandardMaterial;
              const key = typeof sm.customProgramCacheKey === 'function' ? sm.customProgramCacheKey() : '';
              const vc = sm.vertexColors ? ' vc' : '';
              const col = sm.color ? `#${sm.color.getHexString()}` : '';
              return `[${i}${vc}${key ? ` ${key}` : ''} ${col}]`;
            });
            let extra = '';
            if (String(o.name).startsWith('siege-fragment-')) {
              const groups = mesh.geometry?.groups ?? [];
              const seed = mesh.geometry?.getAttribute('aShardSeed');
              const color = mesh.geometry?.getAttribute('color');
              extra = ` groups=${groups.map((g) => `${g.materialIndex}:${g.count}`).join(',')} seed=${seed ? seed.getX(0).toFixed(3) : '-'} colorAttr=${color ? 'y' : 'n'}`;
            }
            lines.push(`${o.type}:${o.name || '<anon>'} vis=${o.visible} ${bits.join(' ')}${extra}`);
          });
          return lines;
        },
        hideByName: (name, visible) => {
          const obj = scene.getObjectByName(name);
          if (obj) obj.visible = visible;
        },
        setFakeShadow: (on) => {
          const g = scene.getObjectByName('particle-lab-ground') as THREE.Mesh | undefined;
          const u = (g?.material as THREE.ShaderMaterial | undefined)?.uniforms;
          if (!u?.uShadowStrength) return;
          if (on) u.uShadowStrength.value = fakeShadowDefaultRef.current;
          else u.uShadowStrength.value = 0;
        },
        setKeyCastShadow: (on) => {
          if (keyLightRef.current) keyLightRef.current.castShadow = on;
        },
        setReceiveShadow: (name, on) => {
          const obj = scene.getObjectByName(name) as THREE.Mesh | undefined;
          if (obj) obj.receiveShadow = on;
        },
        setToneMapping: (kind, exposure) => {
          gl.toneMapping =
            kind === 'agx' ? THREE.AgXToneMapping : THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = exposure;
        },
        dumpGroundUniforms: () => {
          const g = scene.getObjectByName('particle-lab-ground') as THREE.Mesh | undefined;
          const u = (g?.material as THREE.ShaderMaterial | undefined)?.uniforms;
          if (!u) return { found: false };
          const col = (slot: { value?: THREE.Color } | undefined) =>
            slot?.value ? `#${slot.value.getHexString()}` : null;
          return {
            found: true,
            visible: g?.visible ?? null,
            receiveShadow: g?.receiveShadow ?? null,
            toneMapped: (g?.material as THREE.ShaderMaterial).toneMapped,
            transparent: (g?.material as THREE.ShaderMaterial).transparent,
            premultipliedAlpha: (g?.material as THREE.ShaderMaterial).premultipliedAlpha,
            blending: (g?.material as THREE.ShaderMaterial).blending,
            blendSrc: (g?.material as THREE.ShaderMaterial).blendSrc,
            blendDst: (g?.material as THREE.ShaderMaterial).blendDst,
            uDepositOnly: u.uDepositOnly?.value ?? null,
            uShadowStrength: u.uShadowStrength?.value ?? null,
            uGroundColor: col(u.uGroundColor),
            uLightColor: col(u.uLightColor),
            uAmbient: col(u.uAmbient),
            uShadowCap: u.uShadowCap?.value ?? null,
            uDustCover: u.uDustCover?.value ?? null,
            uDepositStain: u.uDepositStain?.value ?? null,
            uFilmTint: u.uFilmTint?.value
              ? [
                  +(u.uFilmTint.value as THREE.Vector3).x.toFixed(3),
                  +(u.uFilmTint.value as THREE.Vector3).y.toFixed(3),
                  +(u.uFilmTint.value as THREE.Vector3).z.toFixed(3),
                ]
              : null,
            uShadowMul: u.uShadowMul?.value
              ? [
                  +(u.uShadowMul.value as THREE.Vector3).x.toFixed(3),
                  +(u.uShadowMul.value as THREE.Vector3).y.toFixed(3),
                  +(u.uShadowMul.value as THREE.Vector3).z.toFixed(3),
                ]
              : null,
            keyCastShadow: keyLightRef.current?.castShadow ?? null,
            siegeGroundReceive: (scene.getObjectByName('siege-ground') as THREE.Mesh | undefined)
              ?.receiveShadow ?? null,
            toneMapping: gl.toneMapping,
            exposure: gl.toneMappingExposure,
          };
        },
        sampleBrick: (i) => {
          if (i < 0 || i >= rt.brickBodies.length) return null;
          if (rt.fractured[i]) return null;
          const o = i * 7;
          const tf = rt.brickPose;
          return {
            x: tf[o],
            y: tf[o + 1],
            z: tf[o + 2],
            qx: tf[o + 3],
            qy: tf[o + 4],
            qz: tf[o + 5],
            qw: tf[o + 6],
            detached: rt.detached[i] === 1,
            retired: rt.retired[i] === 1,
            awake: rt.caps.readSleepStates ? rt.sleepStates[i] === 1 : undefined,
          };
        },
        sampleFragment: (i) => {
          const body = rt.fragmentPool.bodies[i];
          if (body == null) return null;
          const o = rt.fragmentBuffer.offsetOf(body);
          if (o == null) return null;
          const tf = rt.fragmentBuffer.transforms;
          const rec = rt.fragments.get(body as number);
          const vel = rt.caps.getLinearVelocity ? rt.world.getLinearVelocity(body) : { x: 0, y: 0, z: 0 };
          const faces = fragmentFaceCounts(fragmentMeshesRef.current.get(body as number));
          return {
            x: tf[o],
            y: tf[o + 1],
            z: tf[o + 2],
            qx: tf[o + 3],
            qy: tf[o + 4],
            qz: tf[o + 5],
            qw: tf[o + 6],
            retired: rec?.settled === true,
            settled: rec?.settled === true,
            awake: rt.caps.bodyQueries ? rt.world.isBodyAwake(body) : undefined,
            speed: Math.hypot(vel.x, vel.y, vel.z),
            bodyType: rt.caps.bodyQueries ? rt.world.getBodyType(body) : null,
            innerFaces: faces.innerFaces,
            outerFaces: faces.outerFaces,
          };
        },
        dumpDebris: () => {
          const fragments: SiegeFragmentSample[] = [];
          const bricks: SiegeDebrisDump['bricks'] = [];
          const w = gl.domElement.clientWidth || 1280;
          const h = gl.domElement.clientHeight || 720;
          for (let i = 0; i < rt.fragmentPool.bodies.length; i += 1) {
            const s = bridgeRef.current?.sampleFragment?.(i);
            if (!s) continue;
            const p = projectScreen(s.x, s.y, s.z, camera, w, h);
            fragments.push({ ...s, px: p.sx, py: p.sy });
          }
          for (let i = 0; i < rt.brickBodies.length; i += 1) {
            if (rt.fractured[i]) continue;
            const b = bridgeRef.current?.sampleBrick?.(i);
            if (!b || b.y < -10) continue;
            if (!b.detached && b.y < 2.9) continue;
            const p = projectScreen(b.x, b.y, b.z, camera, w, h);
            bricks.push({
              x: b.x,
              y: b.y,
              z: b.z,
              detached: b.detached,
              retired: b.retired,
              awake: b.awake,
              px: p.sx,
              py: p.sy,
            });
          }
          return {
            fragments,
            chunks: dumpInstancedLive(scene, 'particle-lab-chunks', camera, w, h),
            chips: dumpInstancedLive(scene, 'particle-lab-chips', camera, w, h),
            bricks,
          };
        },
        sampleHeight: (x, z) => {
          const sample = pworldRef.current?.sampleHeightfieldAt(x, z);
          if (!sample) return null;
          return { deposit: sample.deposit, granular: sample.granular, film: sample.film };
        },
        dumpCloud: () => dumpFinesUniforms(scene),
        lastBurst: () => pworldRef.current?.lastBurst() ?? null,
        setLightDir: (x, y, z) => {
          dustDirRef.current = { x, y, z };
          pworldRef.current?.setLightDir(x, y, z);
        },
        setSixWay: (on) => pworldRef.current?.setSixWay(on),
        setGrainsVisible: (on) => pworldRef.current?.setGrainsVisible(on),
      };

      attrsDirtyRef.current = true;
      onReady();
    };

    void boot();

    return () => {
      cancelled = true;
      bridgeRef.current = null;
      fireInternalRef.current = () => false;
      resetInternalRef.current = () => {};
      clearFragmentMeshes(fragmentGroupRef.current, fragmentMeshesRef.current);
      for (const name of ['siege-bricks', 'siege-ground', 'siege-sky', 'siege-cannon', 'siege-fragments']) {
        const obj = scene.getObjectByName(name);
        if (obj) scene.remove(obj);
      }
      if (flashRef.current) {
        scene.remove(flashRef.current.sprite);
        scene.remove(flashRef.current.light);
        flashRef.current = null;
      }
      if (pworldRef.current) {
        pworldRef.current.detach();
        pworldRef.current.dispose();
        pworldRef.current = null;
      }
      for (const d of disposables) d.dispose();
      disposables.length = 0;
      runtimeRef.current = null;
      ledgerRef.current = null;
      gridRef.current = null;
      residueRef.current = null;
      cannonRef.current = null;
      brickMeshRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl, scene]);

  // Click-to-fire: short clicks raycast the wall; drags stay with OrbitControls.
  useEffect(() => {
    const el = gl.domElement;
    let downX = 0;
    let downY = 0;
    let downT = 0;
    const onDown = (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
      downT = performance.now();
    };
    const onUp = (e: PointerEvent) => {
      if (performance.now() - downT > 400) return;
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > 5) return;
      const rect = el.getBoundingClientRect();
      ndc.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const mesh = brickMeshRef.current;
      let point: THREE.Vector3 | null = null;
      if (mesh) {
        const hits = raycaster.intersectObject(mesh, false);
        if (hits.length > 0) point = hits[0].point.clone();
      }
      if (!point) {
        const t = -raycaster.ray.origin.z / (raycaster.ray.direction.z || -1e-6);
        if (Number.isFinite(t) && t > 0) {
          point = raycaster.ray.origin.clone().addScaledVector(raycaster.ray.direction, t);
        }
      }
      if (point) fireInternalRef.current(point);
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    const stopCtx = (e: Event) => e.preventDefault();
    el.addEventListener('contextmenu', stopCtx);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('contextmenu', stopCtx);
    };
  }, [gl, camera, ndc, raycaster]);

  useFrame((state, rawDt) => {
    const rt = runtimeRef.current;
    const dt = Math.min(0.05, rawDt);
    if (!rt) return;
    rt.slowMo = slowMoRef.current;
    rt.charge = chargeRef.current;
    const simDt = slowMoRef.current ? dt * 0.25 : dt;
    const session = sessionRef.current;
    if (!session) return;
    session.advance(dt);
    const fanouts = session.drainEvents().map((envelope) => envelope.event);
    const pw = pworldRef.current;
    const mesh = brickMeshRef.current;
    const ledger = ledgerRef.current;
    const grid = gridRef.current;

    for (const f of fanouts) {
      if (!pw) continue;
      if (f.kind === 'wall') {
        const e = f.event.energy;
        burstCountRef.current += 1;
        pw.burst({
          position: [f.event.wx, f.event.wy, f.event.wz],
          direction: [-f.event.dx * 0.4, 0.82 + e * 0.04, 0.62],
          energy: Math.min(4.4, e * 0.9),
          mass: 0.22 * e,
          profile: SIEGE_BRICK,
          surfaceNormal: [0, 0, 1],
          impact: { kind: 'cannon' },
        });
        if (f.detached.length > 0) {
          const nEmit = Math.min(DETACH_BURST_CAP, f.detached.length);
          const eEach = 0.45 / nEmit;
          const mEach = (0.1 * f.detached.length) / nEmit;
          const tf = rt.brickPose;
          for (let k = 0; k < nEmit; k += 1) {
            const o = f.detached[k] * 7;
            const dust = f.pulverizedVolume[k] ?? 0;
            burstCountRef.current += 1;
            pw.burst({
              position: [tf[o], tf[o + 1], tf[o + 2]],
              direction: [0, 1, 0],
              energy: eEach,
              mass: mEach + dust * 8,
              profile: SIEGE_BRICK,
              impact: { kind: 'cannon', intensity: 0.25, blastFraction: 0.12 },
            });
          }
          lastFanoutRef.current = { detached: f.detached.length, detachBursts: nEmit };
        } else {
          lastFanoutRef.current = { detached: 0, detachBursts: 0 };
        }
        attrsDirtyRef.current = true;
        apiRef.current.lastFanoutJson = JSON.stringify(
          {
            stamp: {
              position: [+f.event.lx.toFixed(2), +f.event.ly.toFixed(2), 0.05],
              direction: [+f.event.dx.toFixed(2), +f.event.dy.toFixed(2), +f.event.dz.toFixed(2)],
              energy: +e.toFixed(2),
              intensity: 0.25,
              blastFraction: 0,
              surfaceNormal: [0, 0, 1],
            },
            burst: {
              position: [+f.event.wx.toFixed(2), +f.event.wy.toFixed(2), +f.event.wz.toFixed(2)],
              energy: +Math.min(4.4, e * 0.9).toFixed(2),
              mass: +(0.3 * e).toFixed(2),
              profile: 'brick',
            },
            detached: f.detached.length,
            removedDelta: f.stamp?.removedDelta ?? 0,
            newPaths: f.stamp?.newPaths ?? 0,
          },
          null,
          2,
        );
      } else {
        burstCountRef.current += 1;
        pw.burst({
          position: [f.event.wx, 0.06, f.event.wz],
          direction: [f.event.dx * 0.2, 1, f.event.dz * 0.2],
          energy: Math.min(0.9, f.event.speed / 26),
          mass: 0.1,
          profile: getProfile('concrete'),
          impact: { kind: 'cannon' },
        });
      }
    }

    // Instance matrices track physics every frame; ledger attributes are dirty-gated.
    if (mesh && ledger && grid) {
      const tf = rt.buffer.transforms;
      const n = rt.brickBodies.length;
      const tintApplied = tintAppliedRef.current;
      const brickScale = brickScaleRef.current;
      for (let i = 0; i < n; i += 1) {
        const o = i * 7;
        dummy.position.set(tf[o], tf[o + 1], tf[o + 2]);
        physicsQuat.set(tf[o + 3], tf[o + 4], tf[o + 5], tf[o + 6]);
        if (physicsQuat.lengthSq() < 1e-8) physicsQuat.identity();
        else physicsQuat.normalize();
        jitterEuler.set(
          (hash01(ledger.bricks[i].seed, 21) - 0.5) * 0.016,
          (hash01(ledger.bricks[i].seed, 22) - 0.5) * 0.016,
          (hash01(ledger.bricks[i].seed, 23) - 0.5) * 0.013,
        );
        jitterQuat.setFromEuler(jitterEuler);
        dummy.quaternion.copy(physicsQuat).multiply(jitterQuat);
        if (rt.fractured[i] || tf[o + 1] < -10) dummy.scale.set(0, 0, 0);
        else dummy.scale.set(brickScale.x, brickScale.y, brickScale.z);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        if (tintApplied && rt.detached[i] === 1 && tintApplied[i] === 0) {
          const t = rubbleTintsRef.current[i];
          tmpColor.setRGB(t[0], t[1], t[2]);
          mesh.setColorAt(i, tmpColor);
          tintApplied[i] = 1;
          if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        }
      }
      mesh.instanceMatrix.needsUpdate = true;

      if (attrsDirtyRef.current) {
        attrsDirtyRef.current = false;
        const residueTexHolder = residueRef.current;
        if (residueTexHolder) {
          const baked = bakeResidueMap(rt.residue, rt.stamps, (() => {
            const spec = grid.spec;
            const padX = spec.pitchX * 0.55;
            const slabW = spec.actualWidth + padX;
            const left = spec.originX + spec.actualWidth * 0.5 + padX * 0.15 - slabW * 0.5;
            return { x0: left, y0: -spec.joint, x1: left + slabW, y1: spec.actualHeight + spec.joint };
          })());
          residueTexHolder.data.set(baked);
          residueTexHolder.tex.needsUpdate = true;
        }
        writeInstAttrs(mesh, ledger, rt, rt.residue ?? emptyResidue(n));
      }
    }

    const fragGroup = fragmentGroupRef.current;
    const fragMats = fragmentMatsRef.current;
    if (fragGroup && fragMats) {
      const meshes = fragmentMeshesRef.current;
      for (const [id, fragMesh] of meshes) {
        if (!rt.fragments.has(id)) {
          fragGroup.remove(fragMesh);
          disposeFragmentMesh(fragMesh);
          meshes.delete(id);
        }
      }
      const ftf = rt.fragmentBuffer.transforms;
      for (const body of rt.fragmentPool.bodies) {
        const id = body as number;
        let fragMesh = meshes.get(id);
        if (!fragMesh) {
          const rec = rt.fragments.get(id);
          if (!rec) continue;
          const geom = cellToBufferGeometry(rec.cell);
          const c = rec.cell.centroid;
          stampShardVariance(geom, id, c.x, c.y, c.z);
          fragMesh = new THREE.Mesh(geom, fragMats);
          // Wall/bricks/cannon own the shadow map. Unique hull casters
          // at barrage scale (80–90) blow the PCF budget.
          fragMesh.castShadow = false;
          fragMesh.receiveShadow = false;
          fragMesh.name = `siege-fragment-${id}`;
          fragMesh.scale.setScalar(0.99);
          fragGroup.add(fragMesh);
          meshes.set(id, fragMesh);
        }
        const o = rt.fragmentBuffer.offsetOf(body);
        if (o == null) continue;
        const x = ftf[o];
        const y = ftf[o + 1];
        const z = ftf[o + 2];
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
        const oob = y < -2 || (y < 0.3 && (Math.abs(x) > 14 || z < -7 || z > 16));
        fragMesh.visible = !oob;
        if (oob) continue;
        fragMesh.position.set(x, y, z);
        physicsQuat.set(ftf[o + 3], ftf[o + 4], ftf[o + 5], ftf[o + 6]);
        if (physicsQuat.lengthSq() < 1e-8) physicsQuat.identity();
        else physicsQuat.normalize();
        fragMesh.quaternion.copy(physicsQuat);
      }
    }

    // Cannon aiming, recoil kick, flash decay.
    const cn = cannonRef.current;
    if (cn) {
      const target = controlsRef.current?.target;
      const dx = (target?.x ?? 0) - cn.group.position.x;
      const dy = (target?.y ?? 1.35) - 0.62 - cn.group.position.y;
      const dz = (target?.z ?? 0) - cn.group.position.z;
      const yaw = Math.atan2(dx, dz);
      const pitch = Math.atan2(dy, Math.hypot(dx, dz));
      cn.group.rotation.y += (yaw - cn.group.rotation.y) * Math.min(1, dt * 6);
      cn.barrelPivot.rotation.x += (-pitch - cn.barrelPivot.rotation.x) * Math.min(1, dt * 6);
      if (recoilRef.current > 0.002) {
        recoilRef.current *= Math.exp(-dt * 9);
        cn.barrelPivot.position.z = -recoilRef.current * 0.2;
        cn.barrelPivot.rotation.x -= recoilRef.current * 0.1;
      } else {
        cn.barrelPivot.position.z = 0;
      }
    }
    const fl = flashRef.current;
    if (fl && fl.sprite.visible) {
      fl.light.intensity *= Math.exp(-dt * 26);
      fl.sprite.material.opacity -= dt * 9;
      if (fl.light.intensity < 0.4 || fl.sprite.material.opacity <= 0) {
        fl.sprite.visible = false;
        fl.light.intensity = 0;
        fl.sprite.material.opacity = 1;
      }
    }

    if (pw) {
      pw.setSixWay(true);
      pw.setLightDir(dustDirRef.current.x, dustDirRef.current.y, dustDirRef.current.z);
      pw.step(simDt, state.camera);
    }

    // Cinematic auto-orbit.
    const controls = controlsRef.current;
    if (controls && autoOrbitRef.current) {
      const cam = state.camera;
      tmpVec.copy(cam.position).sub(controls.target);
      const r = Math.hypot(tmpVec.x, tmpVec.z);
      const ang = Math.atan2(tmpVec.x, tmpVec.z) + dt * 0.14;
      tmpVec.x = Math.sin(ang) * r;
      tmpVec.z = Math.cos(ang) * r;
      cam.position.copy(controls.target).add(tmpVec);
      controls.update();
    }

    // FPS + bridges.
    const acc = fpsAccRef.current;
    acc.frames += 1;
    const now = performance.now();
    if (now - acc.t0 >= 400) {
      acc.fps = (acc.frames * 1000) / (now - acc.t0);
      acc.frames = 0;
      acc.t0 = now;
    }
    window.__siegeState = {
      ...(window.__siegeState ?? {}),
      canvasReady: true,
      physicsReady: true,
      ready: Boolean(mesh && pw),
      brickCount: rt.brickBodies.length,
      cols: SIEGE_COLS,
      rows: SIEGE_ROWS,
      awakeCount: rt.awakeCount,
      awakeBrickCount: rt.awakeBrickCount,
      detachedCount: rt.detachedCount,
      retiredCount: rt.retiredCount,
      stamps: rt.stamps.length,
      bursts: burstCountRef.current,
      fps: Number(acc.fps.toFixed(1)),
      simTime: +rt.simTime.toFixed(3),
      stepMs: +rt.stepMs.toFixed(2),
      ...(import.meta.env.DEV
        ? {
            lastFanoutDetached: lastFanoutRef.current.detached,
            lastFanoutDetachBursts: lastFanoutRef.current.detachBursts,
            pileVolume: pw?.getStats().pileVolume ?? 0,
            liveFines: pw?.getStats().liveFines ?? 0,
            liveGrains: pw?.getStats().liveGrains ?? 0,
            sixWay: pw?.getSixWay() ?? null,
            fragmentCount: rt.fragmentCount,
            fragmentsSpawnedTotal: rt.fragmentsSpawnedTotal,
            awakeFragmentCount: rt.awakeFragmentCount,
          }
        : {}),
    };
    if (now - lastBridgeRef.current >= 100) {
      lastBridgeRef.current = now;
      onHud({
        ready: Boolean(mesh && pw),
        physicsReady: true,
        fps: acc.fps,
        awakeCount: rt.awakeCount,
        awakeBrickCount: rt.awakeBrickCount,
        detachedCount: rt.detachedCount,
        stamps: rt.stamps.length,
        bursts: burstCountRef.current,
        simTime: rt.simTime,
        stepMs: rt.stepMs,
      });
    }
  });

  return (
    <>
      <hemisphereLight args={['#4a5680', '#6e4a30', 0.58]} />
      <ambientLight intensity={0.04} color="#3a2c22" />
      <directionalLight
        ref={keyLightRef}
        name="siege-key-light"
        position={[KEY_LIGHT.x, KEY_LIGHT.y, KEY_LIGHT.z]}
        intensity={2.55}
        color="#ffd2a8"
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-left={-12}
        shadow-camera-right={12}
        shadow-camera-top={12}
        shadow-camera-bottom={-6}
        shadow-camera-near={2}
        shadow-camera-far={42}
        shadow-bias={-0.00022}
        shadow-normalBias={0.04}
        shadow-radius={3.2}
      />
      <directionalLight
        name="siege-rim-light"
        position={[RIM_LIGHT.x, RIM_LIGHT.y, RIM_LIGHT.z]}
        intensity={2.05}
        color="#97a8d4"
      />

      <fogExp2 attach="fog" args={[SIEGE_FOG_COLOR, SIEGE_FOG_DENSITY]} />
      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableDamping
        dampingFactor={0.08}
        target={[0, 1.35, 0]}
        minDistance={3.4}
        maxDistance={34}
        maxPolarAngle={Math.PI * 0.495}
      />
    </>
  );
}

export default function SiegeLab({ onBack }: { onBack: () => void }) {
  const isMobile = useViewportWidth() < MOBILE_BREAKPOINT;
  const [hud, setHud] = useState<SiegeHudState>({
    ready: false,
    physicsReady: false,
    fps: 0,
    awakeCount: 0,
    awakeBrickCount: 0,
    detachedCount: 0,
    stamps: 0,
    bursts: 0,
    simTime: 0,
    stepMs: 0,
  });
  const [charge, setCharge] = useState(0.75);
  const [slowMo, setSlowMo] = useState(false);
  const [autoOrbit, setAutoOrbit] = useState(false);
  const [apiOpen, setApiOpen] = useState(false);
  const [, setTick] = useState(0);

  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const chargeRef = useRef(charge);
  const slowMoRef = useRef(slowMo);
  const autoOrbitRef = useRef(autoOrbit);
  const bridgeRef = useRef<SiegeBridge | null>(null);
  const sessionRef = useRef<SiegePageSession | null>(null);
  const apiPanelRef = useRef({ lastFanoutJson: '' });
  const barrageTokenRef = useRef(0);

  chargeRef.current = charge;
  slowMoRef.current = slowMo;
  autoOrbitRef.current = autoOrbit;

  useEffect(() => {
    window.__siegeProbe = {
      fireAt: (x, y, z) => bridgeRef.current?.fireAt(x, y, z),
      barrage: (n = 5) => {
        const token = ++barrageTokenRef.current;
        let fired = 0;
        const tick = () => {
          if (token !== barrageTokenRef.current || fired >= n) return;
          bridgeRef.current?.fireAt((Math.random() - 0.5) * 5.4, 0.7 + Math.random() * 1.7, 0);
          fired += 1;
          setTimeout(tick, 320 + Math.random() * 180);
        };
        tick();
      },
      reset: () => bridgeRef.current?.reset(),
      setCharge: (v) => {
        const c = Math.min(1, Math.max(0.05, Number(v)));
        chargeRef.current = c;
        setCharge(c);
      },
      setSlowMo: (v) => setSlowMo(Boolean(v)),
      setAutoOrbit: (v) => setAutoOrbit(Boolean(v)),
    setCamera: (px, py, pz, tx = 0, ty = 1.35, tz = 0) => {
      const c = controlsRef.current;
      if (!c) return;
      const dist = Math.hypot(px - tx, py - ty, pz - tz);
      if (dist < c.minDistance) c.minDistance = Math.max(0.28, dist * 0.85);
      c.object.position.set(px, py, pz);
      c.target.set(tx, ty, tz);
      c.update();
    },
    dumpScene: () => bridgeRef.current?.dumpScene?.() ?? [],
    hideByName: (name, visible) => bridgeRef.current?.hideByName?.(name, visible),
    setFakeShadow: (on) => bridgeRef.current?.setFakeShadow?.(on),
    setKeyCastShadow: (on) => bridgeRef.current?.setKeyCastShadow?.(on),
    setReceiveShadow: (name, on) => bridgeRef.current?.setReceiveShadow?.(name, on),
    setToneMapping: (kind, exposure) => bridgeRef.current?.setToneMapping?.(kind, exposure),
    dumpGroundUniforms: () => bridgeRef.current?.dumpGroundUniforms?.() ?? null,
      getCamera: () => {
        const c = controlsRef.current;
        if (!c) return null;
        return {
          px: +c.object.position.x.toFixed(3),
          py: +c.object.position.y.toFixed(3),
          pz: +c.object.position.z.toFixed(3),
          tx: +c.target.x.toFixed(3),
          ty: +c.target.y.toFixed(3),
          tz: +c.target.z.toFixed(3),
        };
      },
      ...(import.meta.env.DEV
        ? {
            sampleBrick: (i: number) => bridgeRef.current?.sampleBrick?.(i) ?? null,
            sampleFragment: (i: number) => bridgeRef.current?.sampleFragment?.(i) ?? null,
            dumpDebris: () => bridgeRef.current?.dumpDebris?.() ?? null,
            sampleHeight: (x: number, z: number) => bridgeRef.current?.sampleHeight?.(x, z) ?? null,
            dumpCloud: () => bridgeRef.current?.dumpCloud?.() ?? null,
            lastBurst: () => bridgeRef.current?.lastBurst?.() ?? null,
            setLightDir: (x: number, y: number, z: number) => bridgeRef.current?.setLightDir?.(x, y, z),
            setSixWay: (on: boolean) => bridgeRef.current?.setSixWay?.(on),
            setGrainsVisible: (on: boolean) => bridgeRef.current?.setGrainsVisible?.(on),
          }
        : {}),
    };
    return () => {
      delete window.__siegeProbe;
      delete window.__siegeState;
    };
  }, []);

  const fireRandom = useCallback(() => {
    bridgeRef.current?.fireAt((Math.random() - 0.5) * 4.4, 0.8 + Math.random() * 1.4, 0);
  }, []);

  const doBarrage = useCallback(() => {
    window.__siegeProbe?.barrage(5);
  }, []);

  const doReset = useCallback(() => {
    bridgeRef.current?.reset();
    setTick((t) => t + 1);
  }, []);

  return (
    <div className={`siege-page${isMobile ? ' is-mobile' : ''}`} data-testid="siege-lab-page">
      <Canvas
        className="siege-canvas"
        shadows={{ type: THREE.PCFShadowMap }}
        dpr={1}
        camera={{ position: [10.4, 3.9, 12.6], fov: 44, near: 0.1, far: 240 }}
        gl={{
          antialias: true,
          alpha: false,
          toneMapping: THREE.AgXToneMapping,
          toneMappingExposure: SIEGE_EXPOSURE,
          powerPreference: 'high-performance',
        }}
        onCreated={({ gl: renderer }) => {
          renderer.domElement.style.touchAction = 'none';
          renderer.toneMapping = THREE.AgXToneMapping;
          renderer.toneMappingExposure = SIEGE_EXPOSURE;
          renderer.shadowMap.enabled = true;
          renderer.shadowMap.type = THREE.PCFShadowMap;
        }}
      >
        <SiegeScene
          onHud={(patch) => setHud((prev) => ({ ...prev, ...patch }))}
          onReady={() => setTick((t) => t + 1)}
          controlsRef={controlsRef}
          chargeRef={chargeRef}
          slowMoRef={slowMoRef}
          autoOrbitRef={autoOrbitRef}
          bridgeRef={bridgeRef}
          apiRef={apiPanelRef}
          sessionRef={sessionRef}
        />
      </Canvas>

      <header className="siege-topbar">
        <button type="button" className="siege-back" data-testid="siege-back" onClick={onBack}>
          ← Lab
        </button>
        <h1 className="siege-title">Siege Lab</h1>
        <p className="siege-sub">physics × damage × particles — one event</p>
      </header>

      <div className="siege-hud" data-testid="siege-hud">
        <div>
          breach <b>{hud.detachedCount}</b>
        </div>
        <div>
          awake <b>{hud.awakeCount}</b>
        </div>
        <div>
          stamps <b>{hud.stamps}</b>
        </div>
        <div>
          bursts <b>{hud.bursts}</b>
        </div>
        <div>
          fps <b>{hud.fps.toFixed(0)}</b>
        </div>
        <div>
          sim <b>{hud.simTime.toFixed(1)}s</b>
        </div>
      </div>

      {!isMobile && (
        <aside className="siege-panel" data-testid="siege-panel">
          <h2>Charge</h2>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.05}
            value={charge}
            data-testid="charge-slider"
            onChange={(e) => setCharge(Number(e.target.value))}
          />
          <div className="siege-actions">
            <button type="button" data-testid="btn-fire" onClick={fireRandom}>
              FIRE
            </button>
            <button type="button" data-testid="btn-barrage" onClick={doBarrage}>
              BARRAGE
            </button>
          </div>
          <div className="siege-actions">
            <button
              type="button"
              data-testid="btn-slowmo"
              className={slowMo ? 'is-on' : ''}
              aria-pressed={slowMo}
              onClick={() => setSlowMo((v) => !v)}
            >
              SLOW-MO
            </button>
            <button
              type="button"
              data-testid="btn-orbit"
              className={autoOrbit ? 'is-on' : ''}
              aria-pressed={autoOrbit}
              onClick={() => setAutoOrbit((v) => !v)}
            >
              ORBIT
            </button>
          </div>
          <div className="siege-actions">
            <button type="button" data-testid="btn-reset" onClick={doReset}>
              RESET
            </button>
            <button
              type="button"
              data-testid="toggle-api"
              className={apiOpen ? 'is-on' : ''}
              aria-pressed={apiOpen}
              onClick={() => setApiOpen((v) => !v)}
            >
              API
            </button>
          </div>
          {apiOpen && (
            <pre className="siege-api" data-testid="api-panel">
              {apiPanelRef.current.lastFanoutJson ||
                '// fire at the wall — the last\n// stamp()+burst() pair lands here'}
            </pre>
          )}
        </aside>
      )}

      {isMobile && (
        <footer className="siege-dock" data-testid="siege-dock">
          <button type="button" data-testid="dock-fire" onClick={fireRandom}>
            FIRE
          </button>
          <button type="button" data-testid="dock-barrage" onClick={doBarrage}>
            BARRAGE
          </button>
          <button
            type="button"
            className={slowMo ? 'is-on' : ''}
            data-testid="dock-slowmo"
            onClick={() => setSlowMo((v) => !v)}
          >
            SLOW
          </button>
          <button type="button" data-testid="dock-reset" onClick={doReset}>
            RESET
          </button>
        </footer>
      )}

      {!isMobile && <p className="siege-hint">CLICK THE WALL TO FIRE · DRAG TO ORBIT</p>}
    </div>
  );
}
